/* Copyright (c) 2020-present CloudIO Inc */

import {
  IActionProps,
  ActionItemType,
  getManager,
  AnyActionItem,
  isEmpty,
  ActionManager,
  ITriggerProps,
  TriggerItemType,
  Item,
  TriggerProps,
} from 'componentregistry';
import stringify from 'json-stable-stringify';
import Logger from 'utils/Logger';

export const ResolvedPromise = Promise.resolve(true);
export const ResolvedFalsePromise = Promise.resolve(false);

export const processChildActions = (
  props: IActionProps<ActionItemType>,
): Promise<boolean> => {
  const { appUid, platform, itemId, pageId } = props;
  const actions = platform.getChildrenItemsById(
    appUid,
    pageId,
    itemId,
    (item): boolean => {
      const mgr = getManager(item.type);
      if (mgr.managerType !== 'Action') {
        return false;
      }
      const action = item as AnyActionItem;
      if (
        (
          [
            'ActionSetAggregateAttribute',
            'ActionSetDistinct',
            'ActionSetGroupByAttribute',
            'ActionSetProjection',
            'ActionSetQueryFilter',
            'ActionSetSimpleQueryFilter',
            'ActionSetSort',
            'ActionSet',
          ] as ActionItemType[]
        ).includes(action.type)
      ) {
        return false;
      }
      return true;
    },
  ) as AnyActionItem[];
  return processGivenChildActions(props, actions);
};

export const processGivenChildActions = (
  props: IActionProps<ActionItemType>,
  actions: AnyActionItem[],
): Promise<boolean> => {
  const { appUid, platform, itemId, pageId, nestedActionCounter } = props;
  if (actions.length === 0) return ResolvedPromise;
  if (nestedActionCounter && nestedActionCounter > 10) {
    return Promise.reject(
      Error(`Too many nested actions in Action [Item ID: ${itemId}]`),
    );
  }

  const canExecute = (action: AnyActionItem) => {
    if (isEmpty(action.props.disableCondition)) {
      return true;
    }
    const mgr = getManager(action.type);
    const result = platform.evalBoolean(
      appUid,
      pageId,
      action.itemId,
      'disableCondition',
      { ...props },
      action.props.disableCondition,
    );
    if (!result && Logger.isLogEnabled()) {
      Logger.log({
        itemType: mgr.type,
        status: 'skipped',
        itemId: action.itemId,
        msg: `[${action.props.disableCondition}] is false`,
      });
    }
    return result;
  };
  if (Logger.isLogEnabled()) {
    Logger.increaseLevel();
  }
  try {
    // TODO: filter only enabled actions
    return actions.reduce<Promise<boolean>>(
      (promise, child) =>
        promise.then(() => {
          if (!canExecute(child)) return true;
          const mgr = getManager(
            child.type,
          ) as unknown as ActionManager<ActionItemType>;

          const { action } = mgr;
          const start = Logger.isLogEnabled() ? performance.now() : 0;
          if (!action) {
            platform.showError(`Missing action ${child.type}!`, {
              itemId,
              appUid,
              pageId,
            });
            return false;
          }
          const _props: Record<string, unknown> = {
            ...props,
            itemType: mgr.type,
            nestedActionCounter: nestedActionCounter + 1,
            ...child.props,
            itemId: child.itemId,
          };
          delete _props.platform;
          if (Logger.isLogEnabled()) {
            Logger.log({
              itemType: mgr.type,
              status: 'starting',
              itemId: child.itemId,
              msg: `${stringify(_props)}`,
            });
          }
          // @ts-ignore TODO
          return action({
            ...props,
            itemType: mgr.type,
            nestedActionCounter: nestedActionCounter + 1,
            ...child.props,
            itemId: child.itemId,
          }).finally(() => {
            if (Logger.isLogEnabled()) {
              const ms = Math.round(performance.now() - start);
              Logger.log({
                itemType: mgr.type,
                status: 'complete',
                itemId: child.itemId,
                msg: child.props.disableCondition
                  ? `${ms}ms [${child.props.disableCondition}] is true`
                  : `${ms}ms`,
              });
            }
          });
        }),
      ResolvedPromise,
    );
  } finally {
    if (Logger.isLogEnabled()) {
      Logger.decreaseLevel();
    }
  }
};

export const processChildActionInParallel = async (
  props: IActionProps<ActionItemType>,
): Promise<boolean> => {
  const { appUid, platform, itemId, pageId, nestedActionCounter } = props;
  if (nestedActionCounter && nestedActionCounter > 10) {
    return Promise.reject(
      Error(`Too many nested actions in Action [Item ID: ${itemId}]`),
    );
  }
  const actions = platform.getChildrenItemsById(
    appUid,
    pageId,
    itemId,
    (item): boolean => {
      const mgr = getManager(item.type);
      if (mgr.managerType !== 'Action') {
        return false;
      }
      const action = item as AnyActionItem;
      if (
        (
          [
            'ActionSetAggregateAttribute',
            'ActionSetDistinct',
            'ActionSetGroupByAttribute',
            'ActionSetProjection',
            'ActionSetQueryFilter',
            'ActionSetSimpleQueryFilter',
            'ActionSetSort',
            'ActionSet',
          ] as ActionItemType[]
        ).includes(action.type)
      ) {
        return false;
      }
      return true;
    },
  ) as AnyActionItem[];

  if (actions.length === 0) return ResolvedPromise;

  const canExecute = (action: AnyActionItem) => {
    if (isEmpty(action.props.disableCondition)) {
      return true;
    }
    const mgr = getManager(action.type);
    const result = platform.evalBoolean(
      appUid,
      pageId,
      action.itemId,
      'disableCondition',
      { ...props },
      action.props.disableCondition,
    );
    if (!result && Logger.isLogEnabled()) {
      Logger.log({
        itemType: mgr.type,
        status: 'skipped',
        itemId: action.itemId,
        msg: `[${action.props.disableCondition}] is false`,
      });
    }
    return result;
  };
  if (Logger.isLogEnabled()) {
    Logger.increaseLevel();
  }
  try {
    await Promise.all(
      actions.map(async (child) => {
        if (!canExecute(child)) return true;
        const mgr = getManager(
          child.type,
        ) as unknown as ActionManager<ActionItemType>;

        const { action } = mgr;
        const start = Logger.isLogEnabled() ? performance.now() : 0;
        if (!action) {
          platform.showError(`Missing action ${child.type}!`, {
            itemId,
            appUid,
            pageId,
          });
          return false;
        }
        const _props: Record<string, unknown> = {
          ...props,
          itemType: mgr.type,
          nestedActionCounter: nestedActionCounter + 1,
          ...child.props,
          itemId: child.itemId,
        };
        delete _props.platform;
        if (Logger.isLogEnabled()) {
          Logger.log({
            itemType: mgr.type,
            status: 'starting',
            itemId: child.itemId,
            msg: `${stringify(_props)}`,
          });
        }
        // @ts-ignore TODO
        return action({
          ...props,
          itemType: mgr.type,
          nestedActionCounter: nestedActionCounter + 1,
          ...child.props,
          itemId: child.itemId,
        }).finally(() => {
          if (Logger.isLogEnabled()) {
            const ms = Math.round(performance.now() - start);
            Logger.log({
              itemType: mgr.type,
              status: 'complete',
              itemId: child.itemId,
              msg: child.props.disableCondition
                ? `${ms}ms [${child.props.disableCondition}] is true`
                : `${ms}ms`,
            });
          }
        });
      }),
    );
  } finally {
    if (Logger.isLogEnabled()) {
      Logger.decreaseLevel();
    }
  }
  return true;
};

export const processChildTriggers = (
  props: ITriggerProps,
  trigger: TriggerItemType,
  filterFn?: (child: Item<TriggerItemType>) => boolean,
): Promise<boolean> => {
  const { appUid, itemId, pageId, platform } = props;
  let triggers = platform.getChildrenOfTypeById(
    appUid,
    pageId,
    itemId,
    trigger,
  ) as Item<TriggerItemType>[];
  triggers = triggers.filter((t) => {
    if (isEmpty(t.props.disableCondition)) {
      return true;
    }
    const result = platform.evalBoolean(
      appUid,
      pageId,
      itemId,
      'disableCondition',
      { ...props },
      t.props.disableCondition,
    );
    if (!result && Logger.isLogEnabled()) {
      Logger.log({
        itemType: trigger,
        status: 'skipped',
        itemId,
        msg: `[${t.props.disableCondition}] is false`,
      });
    }
    return result;
  });
  if (filterFn) {
    triggers = triggers.filter((t) => filterFn(t));
  }
  if (triggers.length === 0) {
    return ResolvedPromise;
  }
  const mgr = getManager(trigger);
  if (!mgr || mgr.managerType !== 'Trigger') {
    throw Error(`Unknown Trigger ${trigger} passed to processChildTriggers`);
  }
  const triggerFn = mgr.trigger;
  return triggers.reduce<Promise<boolean>>((promise, child) => {
    const newProps = {
      ...props,
      nestedActionCounter: 0,
      ...child.props,
      itemId: child.itemId,
    };
    return promise.then(() => {
      if (triggerFn) {
        if (Logger.isLogEnabled()) {
          Logger.log({
            itemType: trigger,
            status: 'starting',
            itemId: child.itemId,
            msg: child.props.disableCondition
              ? `[${child.props.disableCondition}] is true`
              : undefined,
          });
        }
        const start = Logger.isLogEnabled() ? performance.now() : 0;
        if (Logger.isLogEnabled()) {
          Logger.increaseLevel();
        }
        return triggerFn(newProps).finally(() => {
          if (Logger.isLogEnabled()) {
            Logger.decreaseLevel();
          }
          if (Logger.isLogEnabled()) {
            const ms = Math.round(performance.now() - start);
            Logger.log({
              itemType: trigger,
              itemId: newProps.itemId,
              status: 'complete',
              msg: `${ms}ms`,
            });
          }
        });
      } else {
        return true;
      }
    });
  }, ResolvedPromise);
};

async function TriggersFn(
  props: TriggerProps<TriggerItemType>,
): Promise<boolean> {
  return processChildActions({
    ...props,
    nestedActionCounter: 0,
    itemType: 'ActionAbort', // dummy
  });
}

export default TriggersFn;
